import React, { useState } from 'react'
import {X, Mail, Lock, User} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userName: '',
    otp: ''
  })
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const handleSendOtp = async () => {
    if (!formData.email) {
      alert('Please enter your email')
      return
    }

    setLoading(true)
    try {
      await lumi.auth.sendOtp({
        email: formData.email,
        type: mode === 'register' ? 'register' : 'resetPassword'
      })
      setOtpSent(true)
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      alert('OTP sent to your email')
    } catch (error) {
      console.error('Failed to send OTP:', error)
      alert('Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (mode === 'login') {
        await lumi.auth.signInWithPassword({
          email: formData.email,
          password: formData.password
        })
        alert('Login successful!')
        window.location.href = '/'
      } else if (mode === 'register') {
        if (!otpSent || !formData.otp) {
          alert('Please request and enter OTP code')
          setLoading(false)
          return
        }
        await lumi.auth.signUpWithPassword({
          email: formData.email,
          password: formData.password,
          otp: formData.otp,
          userName: formData.userName
        })
        alert('Registration successful! You are now logged in.')
        window.location.href = '/'
      } else if (mode === 'reset') {
        if (!otpSent || !formData.otp) {
          alert('Please request and enter OTP code')
          setLoading(false)
          return
        }
        await lumi.auth.resetPassword({
          email: formData.email,
          newPassword: formData.password,
          otp: formData.otp
        })
        alert('Password reset successful! You are now logged in.')
        window.location.href = '/'
      }
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Auth error:', error)
      alert('Authentication failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    try {
      await lumi.auth.signInWithOAuth({ provider })
    } catch (error) {
      console.error('OAuth error:', error)
      alert('OAuth login failed')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full">
        <div className="border-b border-slate-800 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              {mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Reset Password'}
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
              />
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  value={formData.userName}
                  onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                  placeholder="Your name"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                />
              </div>
            </div>
          )}

          {mode !== 'reset' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                />
              </div>
            </div>
          )}

          {(mode === 'register' || mode === 'reset') && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">OTP Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.otp}
                    onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                    placeholder="Enter OTP"
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  />
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loading || countdown > 0}
                    className="px-4 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    {countdown > 0 ? `${countdown}s` : 'Send OTP'}
                  </button>
                </div>
              </div>
              {mode === 'reset' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50"
          >
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Reset Password'}
          </button>

          {mode === 'login' && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-slate-900 text-slate-400">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleOAuthLogin('google')}
                className="w-full px-6 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
              >
                Continue with Google
              </button>
            </>
          )}

          <div className="text-center text-sm text-slate-400">
            {mode === 'login' ? (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-electric-purple-400 hover:text-electric-purple-300"
                >
                  Sign up
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => setMode('reset')}
                  className="text-electric-purple-400 hover:text-electric-purple-300"
                >
                  Forgot password?
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-electric-purple-400 hover:text-electric-purple-300"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
